import {
	BN,
	OracleSource,
	PerpMarketConfig,
	PerpMarkets,
	PublicKey,
	SpotMarketConfig,
	SpotMarkets,
} from '@velocity-exchange/sdk';
import {
	ENUM_UTILS,
	SerializedSpotMarketConfig,
	SerializedPerpMarketConfig,
	SerializedRemoteConfigs,
} from '@velocity-exchange/common';
import { sdkConfig } from 'src/environmentVariables/EnvironmentVariables';

export type RemoteConfigs = {
	spotMarketsConfig: SpotMarketConfig[];
	perpMarketsConfig: PerpMarketConfig[];
};

export class ConfigsManager {
	static DEFAULT_CONFIGS: SerializedRemoteConfigs = {
		marketConfigs: {
			devnet: {
				spot: this.serializeSpotMarketsConfig(SpotMarkets.devnet),
				perp: this.serializedPerpMarketsConfig(PerpMarkets.devnet),
			},
			mainnet: {
				spot: this.serializeSpotMarketsConfig(SpotMarkets['mainnet-beta']),
				perp: this.serializedPerpMarketsConfig(PerpMarkets['mainnet-beta']),
			},
		},
	};

	static deserializeSpotMarketsConfig(
		config: SerializedSpotMarketConfig[]
	): SpotMarketConfig[] {
		return config
			.map((c) => {
				if (!c) return null;
				// Strip serum/phoenix/openbook DEX market refs from any legacy
				// serialized payload — `SpotMarketConfig` no longer has these
				// fields after the velocity migration.
				const {
					serumMarket: _serumMarket,
					phoenixMarket: _phoenixMarket,
					openbookMarket: _openbookMarket,
					...rest
				} = c as SerializedSpotMarketConfig & {
					serumMarket?: unknown;
					phoenixMarket?: unknown;
					openbookMarket?: unknown;
				};
				return {
					...rest,
					precision: new BN(c.precision),
					precisionExp: new BN(c.precisionExp),
					oracle: new PublicKey(c.oracle),
					mint: new PublicKey(c.mint),
					oracleSource: ENUM_UTILS.toObj(c.oracleSource) as OracleSource,
				};
			})
			.filter(Boolean) as SpotMarketConfig[];
	}

	static deserializePerpMarketsConfig(
		config: SerializedPerpMarketConfig[]
	): PerpMarketConfig[] {
		return config.map((c) => ({
			...c,
			category: c.category,
			oracle: new PublicKey(c.oracle),
			oracleSource: ENUM_UTILS.toObj(c.oracleSource) as OracleSource,
		}));
	}

	static deserializeRemoteConfigs(
		config: SerializedRemoteConfigs
	): RemoteConfigs {
		const isMainnet = sdkConfig.ENV === 'mainnet-beta';

		return {
			spotMarketsConfig: this.deserializeSpotMarketsConfig(
				config.marketConfigs[isMainnet ? 'mainnet' : 'devnet']?.spot ?? []
			),
			perpMarketsConfig: this.deserializePerpMarketsConfig(
				config.marketConfigs[isMainnet ? 'mainnet' : 'devnet']?.perp ?? []
			),
		};
	}

	static serializeSpotMarketsConfig(
		config: SpotMarketConfig[]
	): SerializedSpotMarketConfig[] {
		return config
			.map((c) =>
				c
					? {
							...c,
							precision: c.precision.toNumber(),
							precisionExp: c.precisionExp.toNumber(),
							oracle: c.oracle.toString(),
							mint: c.mint.toString(),
							oracleSource: ENUM_UTILS.toStr(c.oracleSource),
						}
					: null
			)
			.filter(Boolean) as SerializedSpotMarketConfig[];
	}

	static serializedPerpMarketsConfig(
		config: PerpMarketConfig[]
	): SerializedPerpMarketConfig[] {
		return config.map((c) => ({
			...c,
			oracle: c.oracle.toString(),
			oracleSource: ENUM_UTILS.toStr(c.oracleSource),
		}));
	}

	static filterProductionConfigs(
		config: SerializedRemoteConfigs,
		isProduction: boolean
	): SerializedRemoteConfigs {
		if (!isProduction) {
			return config;
		}

		const filteredConfig = {
			marketConfigs: {
				devnet: {
					spot:
						config.marketConfigs?.devnet?.spot?.filter((c) => c.production) ??
						[],
					perp:
						config.marketConfigs?.devnet?.perp?.filter((c) => c.production) ??
						[],
				},
				mainnet: {
					spot:
						config.marketConfigs?.mainnet?.spot?.filter((c) => c.production) ??
						[],
					perp:
						config.marketConfigs?.mainnet?.perp?.filter((c) => c.production) ??
						[],
				},
			},
		};

		return filteredConfig;
	}
}
